import { API } from "apis";
import Image from "components/common/images/Image";
import PageTitle from "components/common/PageTitle";
import PageLabel from "components/common/paging/PageLabel";
import Pagination from "components/common/paging/Pagination2";
import RowsPerPageSelect from "components/common/RowsPerPageSelect";
import ApplyStatusChip from "components/exhibit/ApplyStatusChip";
import ExhibitSearchOptionsArea from "components/exhibit/ExhibitSearchOptionsArea";
import ExhibitSearchSortSelect from "components/exhibit/ExhibitSearchSortSelect";
import SaleStatusChip from "components/exhibit/SaleStatusChip";
import { PER_PAGE_TYPES, SaleStatusType } from "enums";
import { saveAs } from "file-saver";
import {
  ApplyItemListModel,
  ApplyItemSearchOptions,
  CategoryModel,
  ContextModel,
} from "models";
import qs from "query-string";
import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Link,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { Utils } from "utils";

const ExhibitListPage: React.FC = () => {
  const ctx = useOutletContext<ContextModel>();

  const location = useLocation();
  const navigate = useNavigate();

  // 商品のカテゴリ情報
  const category = React.useRef<CategoryModel[]>([]);
  // 直前に検索を行ったクエリ
  const searchedQuery = React.useRef<string>();
  // 検索オプション
  const options = React.useRef<ApplyItemSearchOptions>({});
  // 検索オプション(直前の記録)
  const optionsPre = React.useRef<ApplyItemSearchOptions>({});

  const [data, setData] = React.useState<ApplyItemListModel>();

  const reload = () => {
    for (let key in options.current) {
      if ((options.current as any)[key] === "") {
        delete (options.current as any)[key];
      }
    }
    const params = qs.stringify(options.current);
    navigate({ pathname: location.pathname, search: params });
  };

  const onSearch = async () => {
    delete options.current.page_no;
    options.current.get_count =  PER_PAGE_TYPES.DEFAULT;

    reload();
  };

  const onChangeSort = async () => {
    delete options.current.page_no;

    reload();
  };

  const onChangePage = async (value: number) => {
    if (value <= 1) {
      delete options.current.page_no;
    } else {
      options.current.page_no = value;
    }

    reload();
  };

  const onCSVExport = async () => {
    try {
      const response = await API.ApplyItem.csvExport(optionsPre.current);
      if (response.data) {
        const blob = new Blob([response.data], {
          type: "text/csv",
        });
        let fileName = "exhibit-list.csv";
        const _ = response.headers["content-disposition"];
        if (_) {
          fileName = decodeURI(_).substring(_.indexOf("''") + 2, _.length);
        }
        saveAs(blob, fileName);
      }
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
  };

  React.useEffect(() => {
    if (searchedQuery.current === location.search) {
      console.log("useEffect => load cancel.");
      return;
    }
    console.log("useEffect => load start. : " + location.search);
    searchedQuery.current = location.search;

    const f = async () => {
      ctx.loading.setState(true);
      if (!ctx.auth.data) {
        await ctx.auth.initialize();
      }
      if (!ctx.limitPrice.data) {
        await ctx.limitPrice.load();
      }

      // optionsを一度クリアしてから、クエリの状態に
      for (let k in options.current) {
        delete (options.current as any)[k];
      }
      const query = qs.parse(location.search);
      if (query.page_no !== undefined) {
        options.current.page_no = Number(query.page_no);
      }
      if (query.sort_order !== undefined) {
        options.current.sort_order = String(query.sort_order);
      }
      if (query.sale_status !== undefined) {
        options.current.sale_status = Number(query.sale_status);
      }
      if (query.apply_status !== undefined) {
        options.current.apply_status = Number(query.apply_status);
      }
      if (query.apply_item_large_category_id !== undefined) {
        options.current.apply_item_large_category_id = String(
          query.apply_item_large_category_id
        );
      }
      if (query.apply_item_medium_category_id !== undefined) {
        options.current.apply_item_medium_category_id = String(
          query.apply_item_medium_category_id
        );
      }
      if (query.apply_item_small_category_id !== undefined) {
        options.current.apply_item_small_category_id = String(
          query.apply_item_small_category_id
        );
      }
      if (query.keyword !== undefined) {
        options.current.keyword = String(query.keyword);
      }
      if (query.get_count !== undefined) {
        options.current.get_count = Number(query.get_count);
      }

      if (category.current.length === 0) {
        // 会社カテゴリ未ロードならロード
        try {
          const response = await API.CategoryItem.list();
          category.current = response.data;
        } catch (err) {
          Utils.errorAction(ctx, err);
        }
      }

      const response = await API.ApplyItem.search(options.current);
      console.log(response);
      setData(response.data);
      // 検索条件を記録
      optionsPre.current = Object.assign({}, options.current);
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });

      ctx.loading.setState(false);
    };
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  if (!data) {
    return <div></div>;
  }

  return (
    <div>
      <Helmet>
        <title>出品一覧</title>
      </Helmet>
      <PageTitle  className="p-2" title="出品一覧" />

      {/* 検索条件 */}
      {/* <div>{JSON.stringify(options.current)}</div> */}
      <ExhibitSearchOptionsArea
        category={category}
        options={options.current}
        onSearch={onSearch}
      />

      <div className="m-4">
        <div className="flex justify-between items-center p-1">
          <div className="flex gap-10">
            <div>
              <RowsPerPageSelect options={options.current} onChange={onChangeSort} />
              <span>を表示</span>
            </div>
            <div>
              <span className="mr-4">並び替え</span>
              <ExhibitSearchSortSelect
                options={options.current}
                onChange={onChangeSort}
                disabled={data.own_item.length === 0}
              />
            </div>
          </div>
          <div className="flex gap-4">
            <button
              className="secondary px-8 py-2 whitespace-nowrap"
              onClick={onCSVExport}
              disabled={data.own_item.length === 0}
            >
              CSV出力
            </button>
            <Link to="/exhibit-entry/">
              <button className="primary px-8 py-2 whitespace-nowrap">
                出品登録
              </button>
            </Link>
          </div>
        </div>

        {data.own_item.length === 0 ? (
          <div className="my-8 flex justify-center">
            <div>検索結果がありません</div>
          </div>
        ) : (
          <>
            <div className="my-2">
              <PageLabel data={data} />
            </div>
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  <th className="text-left w-6/12">商品名</th>
                  <th className="whitespace-nowrap">価格</th>
                  <th className="whitespace-nowrap">販売</th>
                  <th className="whitespace-nowrap">承認</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.own_item.map((item, index) => {
                  return (
                    <tr key={index} className="border-b">
                      <td>
                        <Link to={`/exhibit-entry/${item.apply_item_cd}/`}>
                          <div className="flex items-center group">
                            <Image
                              data={item.apply_item_main_image}
                              width="4rem"
                              minWidth="4rem"
                              height="4rem"
                              default="item.png"
                            />
                            <span className="ml-2 group-hover:text-jhk-blue group-hover:underline">
                              {item.apply_item_name}
                            </span>
                          </div>
                        </Link>
                      </td>
                      <td className="whitespace-nowrap">
                        {item.apply_is_discretionary_price ? (
                          <>-</>
                        ) : (
                          <>
                            {Utils.addComma(item.apply_unit_price)}円
                            <span className="text-sm">(税抜)</span>
                          </>
                        )}
                      </td>
                      <td className="pl-2">
                        <div className="flex justify-center">
                          <SaleStatusChip
                            label={item.sale_status_name}
                            status={item.sale_status}
                          />
                        </div>
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex justify-center">
                          <ApplyStatusChip
                            label={item.apply_status_name}
                            status={item.apply_status}
                          />
                        </div>
                      </td>
                      <td>
                        {item.sale_status !== null &&
                          item.sale_status !== SaleStatusType._2_TEISHI && (
                            <Link to={`/item-detail/${item.apply_item_cd}/`}>
                              <button className="secondary px-4 py-2 whitespace-nowrap text-sm">
                                商品ページを見る
                              </button>
                            </Link>
                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex justify-center m-4">
              <Pagination
                page={options.current?.page_no ?? 1}
                data={data}
                onChange={onChangePage}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default ExhibitListPage;
